/**
 * Onboarding Tour Context - Enhanced Version
 * Mobil ve masaüstü için ayrı turlar, checklist sistemi
 * 
 * Özellikler:
 * - Cihaz bazlı farklı turlar (mobile vs desktop)
 * - Plan bazlı farklı senaryolar (starter, pro, enterprise)
 * - Checklist tabanlı ilerleme takibi
 * - Adım adım interaktif rehberlik
 * - Spotlight ve tooltip desteği
 * - İlerleme takibi ve kaydetme
 * - Klavye navigasyonu (ESC ile çıkış, Arrow keys ile gezinme)
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const OnboardingContext = createContext(null);

// Cihaz tespiti
const isMobileDevice = () => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Checklist items
const ONBOARDING_CHECKLIST = [
    { id: 'complete_tour', title: 'Tanıtım turunu tamamla', icon: 'fas fa-graduation-cap', completed: false },
    { id: 'add_store', title: 'Mağaza ekle', icon: 'fas fa-store', completed: false, path: '/settings' },
    { id: 'sync_orders', title: 'Siparişleri senkronize et', icon: 'fas fa-sync', completed: false },
    { id: 'view_dashboard', title: 'Dashboard\'u incele', icon: 'fas fa-chart-line', completed: false, path: '/dashboard' }
];

// ==========================================
// MASAÜSTÜ TURLARI
// ==========================================
const DESKTOP_TOUR_SCENARIOS = {
    // Tüm planlar için ortak başlangıç adımları
    common: [
        {
            id: 'welcome',
            target: null,
            type: 'modal',
            title: '🎉 Order Panel\'e Hoş Geldiniz!',
            content: 'Bu kısa tur ile panelin temel özelliklerini öğreneceksiniz. Hazır olduğunuzda "Başla" butonuna tıklayın.',
            position: 'center',
            cta: 'Tura Başla'
        },
        {
            id: 'navigation',
            target: '.admin-nav__tabs',
            type: 'spotlight',
            title: '🧭 Navigasyon Menüsü',
            content: 'Üst menüden tüm sayfalara hızlıca erişebilirsiniz. Her sekme farklı bir özelliğe erişim sağlar.',
            position: 'bottom'
        },
        {
            id: 'dashboard',
            target: '[href="/dashboard"]',
            type: 'spotlight',
            title: '📊 Komuta Merkezi',
            content: 'Ana sayfa, işinizin genel görünümünü sunar. Günlük satışlar, bekleyen siparişler ve önemli metrikleri burada görürsünüz.',
            position: 'bottom'
        },
        {
            id: 'orders',
            target: '[href="/orders"]',
            type: 'spotlight',
            title: '📦 Sipariş Yönetimi',
            content: 'Tüm siparişlerinizi tek yerden yönetin. Filtreleme, toplu işlemler ve kargo takibi bu sayfada.',
            position: 'bottom'
        },
        {
            id: 'questions',
            target: '[href="/questions"]',
            type: 'spotlight',
            title: '💬 Müşteri Soruları',
            content: 'Pazaryerlerinden gelen müşteri sorularını buradan yanıtlayın. AI destekli öneriler ile hızlı cevaplar verin.',
            position: 'bottom'
        },
        {
            id: 'products',
            target: '[href="/products"]',
            type: 'spotlight',
            title: '📦 Ürün Havuzu',
            content: 'Ürünlerinizi merkezi bir havuzda yönetin. Tek bir üründen tüm pazaryerlerine yayın yapın.',
            position: 'bottom'
        },
        {
            id: 'theme-toggle',
            target: '.nav-tab:has(.fa-sun), .nav-tab:has(.fa-moon)',
            type: 'spotlight',
            title: '🌓 Tema Değiştirme',
            content: 'Karanlık ve aydınlık mod arasında geçiş yapabilirsiniz.',
            position: 'bottom'
        },
        {
            id: 'user-menu',
            target: '.user-menu-container',
            type: 'spotlight',
            title: '👤 Kullanıcı Menüsü',
            content: 'Hesap ayarları, raporlar, ekip yönetimi ve çıkış işlemlerine bu menüden erişin.',
            position: 'bottom-left'
        }
    ],

    // Starter paketi için ek adımlar
    starter: [
        {
            id: 'starter-limits',
            target: null,
            type: 'modal',
            title: '⚡ Başlangıç Paketi',
            content: 'Başlangıç paketiniz ile aylık 100 sipariş ve 1 mağaza yönetebilirsiniz. İhtiyacınız artarsa Pro paketine yükseltebilirsiniz.',
            position: 'center',
            badge: 'starter'
        },
        {
            id: 'settings-intro',
            target: '[href="/settings"]',
            type: 'spotlight',
            title: '⚙️ İlk Adım: Mağaza Bağlayın',
            content: 'İlk adım olarak buradan mağazanızı bağlayın. API bilgilerinizi girerek siparişlerinizi otomatik olarak çekmeye başlayın.',
            position: 'bottom',
            action: {
                label: 'Mağaza Ekle',
                path: '/settings'
            }
        }
    ],

    // Pro paketi için ek adımlar
    pro: [
        {
            id: 'pro-features',
            target: null,
            type: 'modal',
            title: '🚀 Pro Paket',
            content: 'Pro paketiniz ile sınırsız sipariş ve 5 mağaza yönetebilirsiniz. Gelişmiş raporlama ve toplu işlemler de dahil!',
            position: 'center',
            badge: 'pro'
        },
        {
            id: 'metrics',
            target: '[href="/metrics"]',
            type: 'spotlight',
            title: '📈 İzleme Paneli',
            content: 'Pro paketinize özel gelişmiş metrikler! Satış trendleri, performans analizleri ve tahminleri buradan takip edebilirsiniz.',
            position: 'bottom'
        },
        {
            id: 'bulk-operations',
            target: null,
            type: 'modal',
            title: '⚡ Toplu İşlemler',
            content: 'Pro paketinizle birden fazla siparişi aynı anda güncelleyebilir, toplu kargo çıkışı yapabilir ve zaman kazanabilirsiniz.',
            position: 'center'
        },
        {
            id: 'settings-pro',
            target: '[href="/settings"]',
            type: 'spotlight',
            title: '⚙️ Mağaza Ayarları',
            content: '5 mağazaya kadar bağlantı yapabilirsiniz. Her mağaza için ayrı API ayarları girebilirsiniz.',
            position: 'bottom',
            action: {
                label: 'Mağazaları Yönet',
                path: '/settings'
            }
        }
    ],

    // Enterprise paketi için ek adımlar
    enterprise: [
        {
            id: 'enterprise-features',
            target: null,
            type: 'modal',
            title: '👑 Enterprise Paket',
            content: 'Enterprise paketiniz ile tüm özelliklere sınırsız erişiminiz var! Özel entegrasyonlar ve 7/24 destek dahil.',
            position: 'center',
            badge: 'enterprise'
        },
        {
            id: 'metrics-enterprise',
            target: '[href="/metrics"]',
            type: 'spotlight',
            title: '📈 Gelişmiş Analitik',
            content: 'Tüm metrikler ve özel raporlara erişiminiz var. AI destekli tahminler ve trend analizleri sunuyoruz.',
            position: 'bottom'
        },
        {
            id: 'team',
            target: '[href="/team"]',
            type: 'spotlight',
            title: '👥 Ekip Yönetimi',
            content: 'Sınırsız ekip üyesi ekleyebilir, rol bazlı yetkiler tanımlayabilirsiniz.',
            position: 'bottom'
        },
        {
            id: 'api-integration',
            target: null,
            type: 'modal',
            title: '🔗 Özel Entegrasyonlar',
            content: 'Enterprise paketinizle kendi sistemlerinize API üzerinden bağlanabilir, özel webhook\'lar tanımlayabilirsiniz.',
            position: 'center',
            action: {
                label: 'API Dökümanları',
                path: '/developers'
            }
        },
        {
            id: 'settings-enterprise',
            target: '[href="/settings"]',
            type: 'spotlight',
            title: '⚙️ Mağaza Ayarları',
            content: 'Sınırsız mağaza bağlantısı yapabilirsiniz. Her pazaryeri için gelişmiş yapılandırma seçenekleri mevcut.',
            position: 'bottom',
            action: {
                label: 'Mağazaları Yönet',
                path: '/settings'
            }
        }
    ],

    // API Ekleme özel turu
    apiSetup: [
        {
            id: 'api-intro',
            target: null,
            type: 'modal',
            title: '🔌 API Bağlantısı',
            content: 'Siparişlerinizi otomatik olarak çekmek için pazaryeri API bilgilerinizi girmeniz gerekiyor. Bu işlem sadece bir kez yapılır.',
            position: 'center'
        },
        {
            id: 'marketplace-select',
            target: '.marketplace-selector, .marketplace-grid',
            type: 'spotlight',
            title: '🏪 Pazaryeri Seçimi',
            content: 'Bağlanmak istediğiniz pazaryerini seçin. Trendyol, Hepsiburada, N11 ve daha fazlası destekleniyor.',
            position: 'right'
        },
        {
            id: 'api-credentials',
            target: '.api-credentials-form, .store-form',
            type: 'spotlight',
            title: '🔑 API Bilgileri',
            content: 'Pazaryeri satıcı panelinizden aldığınız API Key ve Secret bilgilerini buraya girin. Bu bilgiler şifreli olarak saklanır.',
            position: 'left'
        },
        {
            id: 'test-connection',
            target: '.test-connection-btn, .btn-test',
            type: 'spotlight',
            title: '✅ Bağlantı Testi',
            content: 'Bilgilerinizi girdikten sonra "Bağlantıyı Test Et" butonuna tıklayarak API bağlantınızın çalıştığını doğrulayın.',
            position: 'top'
        },
        {
            id: 'api-complete',
            target: null,
            type: 'modal',
            title: '🎉 Tebrikler!',
            content: 'API bağlantınız hazır! Artık siparişleriniz otomatik olarak çekilecek. Mağaza Ayarları\'ndan senkronizasyon aralığını ayarlayabilirsiniz.',
            position: 'center'
        }
    ],

    // Plan Yükseltme Turları
    // Starter -> Pro yükseltme
    upgrade_starter_to_pro: [
        {
            id: 'upgrade-welcome-pro',
            target: null,
            type: 'modal',
            title: '🎉 Pro Pakete Hoş Geldiniz!',
            content: 'Tebrikler! Pro pakete yükselttiniz. Şimdi size yeni özelliklerinizi tanıtalım.',
            position: 'center',
            badge: 'pro'
        },
        {
            id: 'upgrade-unlimited-orders',
            target: null,
            type: 'modal',
            title: '📦 Sınırsız Sipariş',
            content: 'Artık aylık sipariş limitiniz yok! İstediğiniz kadar sipariş yönetebilirsiniz.',
            position: 'center'
        },
        {
            id: 'upgrade-more-stores',
            target: '[href="/settings"]',
            type: 'spotlight',
            title: '🏪 5 Mağaza Bağlantısı',
            content: 'Artık 5 farklı mağazayı aynı anda yönetebilirsiniz. Hemen yeni mağazalarınızı ekleyin!',
            position: 'bottom'
        },
        {
            id: 'upgrade-metrics',
            target: '[href="/metrics"]',
            type: 'spotlight',
            title: '📈 Gelişmiş Metrikler',
            content: 'Pro paketinizle gelişmiş satış analizleri, trend raporları ve performans takibi yapabilirsiniz.',
            position: 'bottom'
        },
        {
            id: 'upgrade-bulk',
            target: '[href="/orders"]',
            type: 'spotlight',
            title: '⚡ Toplu İşlemler',
            content: 'Siparişlerde toplu güncelleme, toplu kargo çıkışı ve toplu etiket yazdırma özelliklerini kullanabilirsiniz.',
            position: 'bottom'
        },
        {
            id: 'upgrade-priority-support',
            target: null,
            type: 'modal',
            title: '🎧 Öncelikli Destek',
            content: 'Pro kullanıcı olarak destek talepleriniz öncelikli olarak işleme alınacak.',
            position: 'center'
        }
    ],

    // Pro -> Enterprise yükseltme
    upgrade_pro_to_enterprise: [
        {
            id: 'upgrade-welcome-enterprise',
            target: null,
            type: 'modal',
            title: '👑 Enterprise Pakete Hoş Geldiniz!',
            content: 'En üst seviye pakete yükselttiniz! Tüm özellikler artık sizin.',
            position: 'center',
            badge: 'enterprise'
        },
        {
            id: 'upgrade-unlimited-stores',
            target: '[href="/settings"]',
            type: 'spotlight',
            title: '🏪 Sınırsız Mağaza',
            content: 'Artık istediğiniz kadar mağaza bağlayabilirsiniz. Tüm pazaryerlerinizi tek yerden yönetin!',
            position: 'bottom'
        },
        {
            id: 'upgrade-team',
            target: '[href="/team"]',
            type: 'spotlight',
            title: '👥 Sınırsız Ekip Üyesi',
            content: 'Ekibinize istediğiniz kadar üye ekleyebilir, rol bazlı yetkiler tanımlayabilirsiniz.',
            position: 'bottom'
        },
        {
            id: 'upgrade-api-access',
            target: null,
            type: 'modal',
            title: '🔗 API Erişimi',
            content: 'Enterprise paketinizle kendi sistemlerinize API üzerinden entegre olabilir, özel webhook\'lar tanımlayabilirsiniz.',
            position: 'center',
            action: {
                label: 'API Dökümanları',
                path: '/developers'
            }
        },
        {
            id: 'upgrade-247-support',
            target: null,
            type: 'modal',
            title: '🎧 7/24 Özel Destek',
            content: 'Artık 7 gün 24 saat özel destek hattımıza erişebilirsiniz. Acil durumlar için size özel destek ekibi atanacak.',
            position: 'center'
        }
    ],

    // Starter -> Enterprise yükseltme
    upgrade_starter_to_enterprise: [
        {
            id: 'upgrade-welcome-enterprise-full',
            target: null,
            type: 'modal',
            title: '👑 Enterprise Pakete Hoş Geldiniz!',
            content: 'Mükemmel bir sıçrama yaptınız! En kapsamlı paketimizle tanışın.',
            position: 'center',
            badge: 'enterprise'
        },
        {
            id: 'upgrade-all-unlimited',
            target: null,
            type: 'modal',
            title: '♾️ Her Şey Sınırsız',
            content: 'Sipariş, mağaza, ekip üyesi... Artık hiçbir limitiniz yok!',
            position: 'center'
        },
        {
            id: 'upgrade-stores-enterprise',
            target: '[href="/settings"]',
            type: 'spotlight',
            title: '🏪 Sınırsız Mağaza',
            content: 'Tüm pazaryerlerinizdeki mağazaları tek panelden yönetin.',
            position: 'bottom'
        },
        {
            id: 'upgrade-metrics-enterprise',
            target: '[href="/metrics"]',
            type: 'spotlight',
            title: '📈 Gelişmiş Analitik',
            content: 'AI destekli tahminler, detaylı trend analizleri ve özel raporlar oluşturabilirsiniz.',
            position: 'bottom'
        },
        {
            id: 'upgrade-team-enterprise',
            target: '[href="/team"]',
            type: 'spotlight',
            title: '👥 Ekip Yönetimi',
            content: 'Sınırsız ekip üyesi ekleyebilir, departmanlara göre rol ve yetkiler tanımlayabilirsiniz.',
            position: 'bottom'
        },
        {
            id: 'upgrade-api-enterprise',
            target: null,
            type: 'modal',
            title: '🔗 API & Entegrasyonlar',
            content: 'Kendi ERP, muhasebe veya e-ticaret sistemlerinize entegre olun. Özel webhook\'lar ve API erişimi dahil.',
            position: 'center',
            action: {
                label: 'API Dökümanları',
                path: '/developers'
            }
        },
        {
            id: 'upgrade-support-enterprise',
            target: null,
            type: 'modal',
            title: '🎧 7/24 Özel Destek',
            content: 'Size özel destek ekibi atanacak. Telefon, e-posta veya canlı destek ile 7/24 ulaşabilirsiniz.',
            position: 'center'
        }
    ]
};

// ==========================================
// MOBİL TURLARI - DAHA KISA VE ÖZ
// ==========================================
const MOBILE_TOUR_SCENARIOS = {
    // Mobil için ortak adımlar - daha kısa ve öz
    common: [
        {
            id: 'welcome-mobile',
            target: null,
            type: 'modal',
            title: '👋 Hoş Geldiniz!',
            content: 'Order Panel mobil uygulamasına hoş geldiniz! Siparişlerinizi her yerden yönetin.',
            position: 'center',
            cta: 'Başla'
        },
        {
            id: 'bottom-nav',
            target: '.mobile-bottom-nav',
            type: 'spotlight',
            title: '📱 Alt Menü',
            content: 'Alt menüden ana sayfalara hızlıca erişin. En çok kullandığınız özellikler burada.',
            position: 'top'
        },
        {
            id: 'dashboard-mobile',
            target: '.mobile-bottom-nav__item[href="/dashboard"]',
            type: 'spotlight',
            title: '📊 Dashboard',
            content: 'İşinizin genel görünümü, satışlar ve önemli metrikler.',
            position: 'top'
        },
        {
            id: 'orders-mobile',
            target: '.mobile-bottom-nav__item[href="/orders"]',
            type: 'spotlight',
            title: '📦 Siparişler',
            content: 'Bekleyen siparişleri görün, durumları güncelleyin.',
            position: 'top'
        },
        {
            id: 'header-actions',
            target: '.mobile-header__actions',
            type: 'spotlight',
            title: '⚡ Hızlı Erişim',
            content: 'Tema değiştirme, bildirimler ve hesap ayarlarına üst menüden erişin.',
            position: 'bottom'
        }
    ],

    // Mobil için starter paketi
    starter: [
        {
            id: 'starter-mobile',
            target: null,
            type: 'modal',
            title: '⚡ Başlangıç Paketi',
            content: 'Aylık 100 sipariş ve 1 mağaza yönetimi. Daha fazla özellik için masaüstünü kullanın.',
            position: 'center',
            badge: 'starter'
        }
    ],

    // Mobil için pro paketi  
    pro: [
        {
            id: 'pro-mobile',
            target: null,
            type: 'modal',
            title: '🚀 Pro Paket',
            content: 'Sınırsız sipariş ve 5 mağaza! Gelişmiş özellikler masaüstünde.',
            position: 'center',
            badge: 'pro'
        }
    ],

    // Mobil için enterprise paketi
    enterprise: [
        {
            id: 'enterprise-mobile',
            target: null,
            type: 'modal',
            title: '👑 Enterprise',
            content: 'Tüm özellikler sınırsız! Gelişmiş yönetim için masaüstünü tercih edin.',
            position: 'center',
            badge: 'enterprise'
        }
    ],

    // API kurulumu mobil
    apiSetup: [
        {
            id: 'api-intro-mobile',
            target: null,
            type: 'modal',
            title: '🔌 API Kurulumu',
            content: 'API kurulumu için masaüstü cihaz kullanmanızı öneriyoruz. Daha kolay bir deneyim için bilgisayarınızdan giriş yapın.',
            position: 'center',
            isLast: true
        }
    ]
};

// Finish step fonksiyonu (mobil/masaüstü için farklı mesaj)
const getFinishStep = (isMobile) => ({
    id: 'finish',
    target: null,
    type: 'modal',
    title: '🎊 Tur Tamamlandı!',
    content: isMobile
        ? 'Temel özellikleri öğrendiniz. Soru işareti simgesinden tekrar erişebilirsiniz.'
        : 'Temel özellikleri öğrendiniz. Her zaman sağ alt köşedeki yardım butonundan bu tura tekrar başlayabilirsiniz.',
    position: 'center',
    isLast: true
});

export const OnboardingProvider = ({ children }) => {
    const [isActive, setIsActive] = useState(false);
    const [currentTour, setCurrentTour] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [steps, setSteps] = useState([]);
    const [userPlan, setUserPlan] = useState('starter');
    const [completedTours, setCompletedTours] = useState([]);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [checklist, setChecklist] = useState(ONBOARDING_CHECKLIST);
    const overlayRef = useRef(null);

    // Cihaz türünü belirle
    useEffect(() => {
        const checkDevice = () => {
            setIsMobile(isMobileDevice());
        };
        checkDevice();
        window.addEventListener('resize', checkDevice);
        return () => window.removeEventListener('resize', checkDevice);
    }, []);

    // Kullanıcının tamamladığı turları localStorage'dan yükle
    useEffect(() => {
        try {
            const saved = localStorage.getItem('onboarding_completed');
            if (saved) {
                setCompletedTours(JSON.parse(saved));
            }
            const savedChecklist = localStorage.getItem('onboarding_checklist');
            if (savedChecklist) {
                setChecklist(JSON.parse(savedChecklist));
            }
        } catch (e) {
            console.error('Onboarding state load error:', e);
        }
    }, []);

    // Tamamlanan turları kaydet
    const saveCompletedTour = useCallback((tourId) => {
        const updated = [...completedTours, tourId];
        setCompletedTours(updated);
        localStorage.setItem('onboarding_completed', JSON.stringify(updated));

        // Checklist'i güncelle
        if (tourId === 'main' || tourId === 'main_mobile') {
            const updatedChecklist = checklist.map(item =>
                item.id === 'complete_tour' ? { ...item, completed: true } : item
            );
            setChecklist(updatedChecklist);
            localStorage.setItem('onboarding_checklist', JSON.stringify(updatedChecklist));
        }

        // Backend'e de kaydet
        fetch('/api/auth/onboarding-complete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tourId, isMobile })
        }).catch(err => console.error('Onboarding save error:', err));
    }, [completedTours, checklist, isMobile]);

    // Checklist item'ı tamamla
    const completeChecklistItem = useCallback((itemId) => {
        const updatedChecklist = checklist.map(item =>
            item.id === itemId ? { ...item, completed: true } : item
        );
        setChecklist(updatedChecklist);
        localStorage.setItem('onboarding_checklist', JSON.stringify(updatedChecklist));
    }, [checklist]);

    // Tur başlat - mobil/masaüstü otomatik seçim
    const startTour = useCallback((tourType = 'main', plan = 'starter', options = {}) => {
        const mobile = isMobile;
        setUserPlan(plan);
        setCurrentTour(tourType + (mobile ? '_mobile' : ''));

        let tourSteps = [];
        const scenarios = mobile ? MOBILE_TOUR_SCENARIOS : DESKTOP_TOUR_SCENARIOS;

        if (tourType === 'main') {
            // Ana tur: ortak adımlar + plan bazlı adımlar
            tourSteps = [
                ...(scenarios.common || []),
                ...(scenarios[plan] || scenarios.starter || []),
                getFinishStep(mobile)
            ];
        } else if (tourType === 'apiSetup') {
            // API kurulum turu
            if (mobile && MOBILE_TOUR_SCENARIOS.apiSetup) {
                tourSteps = [...MOBILE_TOUR_SCENARIOS.apiSetup];
            } else {
                tourSteps = [...(DESKTOP_TOUR_SCENARIOS.apiSetup || [])];
            }
        } else if (tourType.startsWith('upgrade_')) {
            // Plan yükseltme turu - sadece masaüstü
            const upgradeSteps = DESKTOP_TOUR_SCENARIOS[tourType];
            if (upgradeSteps) {
                const upgradeFinish = {
                    id: 'upgrade-finish',
                    target: null,
                    type: 'modal',
                    title: '🎊 Yeni Özellikleriniz Hazır!',
                    content: 'Yeni paketinizin tüm özelliklerini keşfettiniz. Keyifli kullanımlar!',
                    position: 'center',
                    isLast: true
                };
                tourSteps = [...upgradeSteps, upgradeFinish];
            }
        } else if (scenarios[tourType]) {
            // Diğer özel turlar
            tourSteps = [...scenarios[tourType], getFinishStep(mobile)];
        }

        if (tourSteps.length === 0) {
            console.warn('No tour steps found for:', tourType);
            return;
        }

        setSteps(tourSteps);
        setCurrentStep(0);
        setIsActive(true);
        setIsMinimized(false);

        // Body scroll'u kapat
        document.body.style.overflow = 'hidden';
    }, [isMobile]);

    // Turu bitir
    const endTour = useCallback((completed = false) => {
        if (completed && currentTour) {
            saveCompletedTour(currentTour);
        }

        setIsActive(false);
        setCurrentTour(null);
        setCurrentStep(0);
        setSteps([]);
        setIsMinimized(false);

        // Body scroll'u aç
        document.body.style.overflow = '';
    }, [currentTour, saveCompletedTour]);

    // Sonraki adım
    const nextStep = useCallback(() => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            endTour(true);
        }
    }, [currentStep, steps.length, endTour]);

    // Önceki adım
    const prevStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    }, [currentStep]);

    // Belirli adıma git
    const goToStep = useCallback((stepIndex) => {
        if (stepIndex >= 0 && stepIndex < steps.length) {
            setCurrentStep(stepIndex);
        }
    }, [steps.length]);

    // Turu küçült/büyüt
    const toggleMinimize = useCallback(() => {
        setIsMinimized(prev => !prev);
    }, []);

    // Turun tamamlanıp tamamlanmadığını kontrol et
    const isTourCompleted = useCallback((tourId) => {
        // Mobil ve masaüstü versiyonlarını da kontrol et
        return completedTours.includes(tourId) ||
            completedTours.includes(tourId + '_mobile') ||
            completedTours.includes(tourId.replace('_mobile', ''));
    }, [completedTours]);

    // İlk kayıt kontrolü - sadece yeni kayıt olanlar için
    const shouldShowOnboarding = useCallback((plan, isNewUser = false) => {
        // Sadece yeni kayıt olan kullanıcılara göster
        if (!isNewUser) return false;
        return !isTourCompleted('main');
    }, [isTourCompleted]);

    // Plan yükseltme turu başlat
    const startUpgradeTour = useCallback((fromPlan, toPlan) => {
        const tourId = `upgrade_${fromPlan}_to_${toPlan}`;

        // Bu yükseltme turu zaten gösterilmiş mi?
        if (isTourCompleted(tourId)) {
            return false;
        }

        // Tur senaryosu var mı?
        if (!DESKTOP_TOUR_SCENARIOS[tourId]) {
            console.warn('No upgrade tour found for:', tourId);
            return false;
        }

        startTour(tourId, toPlan);
        return true;
    }, [isTourCompleted, startTour]);

    // Keyboard navigasyonu
    useEffect(() => {
        if (!isActive) return;

        const handleKeyDown = (e) => {
            switch (e.key) {
                case 'Escape':
                    endTour(false);
                    break;
                case 'ArrowRight':
                case 'Enter':
                    nextStep();
                    break;
                case 'ArrowLeft':
                    prevStep();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isActive, endTour, nextStep, prevStep]);

    // Mevcut adım bilgisi
    const currentStepData = steps[currentStep] || null;
    const progress = steps.length > 0 ? ((currentStep + 1) / steps.length) * 100 : 0;
    const checklistProgress = checklist.filter(item => item.completed).length / checklist.length * 100;

    const value = {
        // Durum
        isActive,
        isMinimized,
        isMobile,
        currentTour,
        currentStep,
        currentStepData,
        steps,
        progress,
        userPlan,
        completedTours,
        checklist,
        checklistProgress,

        // Aksiyonlar
        startTour,
        startUpgradeTour,
        endTour,
        nextStep,
        prevStep,
        goToStep,
        toggleMinimize,
        isTourCompleted,
        shouldShowOnboarding,
        completeChecklistItem,

        // Ref
        overlayRef
    };

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
};

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
};

export default OnboardingContext;
